import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import TextareaAutosize from "react-textarea-autosize";
import { UserData } from "@/entities/users/types";
import { useTranslation } from "react-i18next";
import { getCurrentTimeUTC } from "@/utils/datetime";
import { cn } from "@/utils/utils";
import { ChatMessage } from "@/entities/messaging/types";
import { useMessaging } from "@/entities/messaging/hooks";
import { IconButton } from "@/components/ui/icon-button";
import { SendButton, Loader, StopGenerating } from "@dreamhub/designsystem/icons";
import { useChat } from "@/components/layouts/Chat/ChatContext";

const DISCONNECT_TIMEOUT = 2000;

interface ChatBottombarProps {
  selectedUser: UserData;
  sendMessage: (newMessage: ChatMessage) => void;
  lastMessageFromSelectedUser: boolean;
  onStopResponse?: () => void;
  isFromHeader?: boolean;
}

export default function NewChatBottomBar({
  selectedUser,
  sendMessage,
  lastMessageFromSelectedUser,
  onStopResponse,
  isFromHeader
}: ChatBottombarProps) {
  const [message, setMessage] = useState("");
  const [shouldDisable, setShouldDisable] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const disconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation();
  const { isConnected } = useMessaging();
  const { isCancelling } = useChat();

  useEffect(() => {
    setHasInitialized(true);
  }, []);

  // Handle connection state optimistically
  useEffect(() => {
    if (!hasInitialized) return;

    if (isConnected) {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setShouldDisable(false);
    } else {
      // When disconnected, start a timer to disable if confirmed
      disconnectTimerRef.current = setTimeout(() => {
        setShouldDisable(true);
      }, DISCONNECT_TIMEOUT);
    }
    return () => {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
      }
    };
  }, [isConnected, hasInitialized]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleSend = () => {
    if (message.trim()) {
      const newMessage: ChatMessage = {
        id: new Date().getTime().toString(),
        user: selectedUser,
        message: message.trim(),
        timestamp: getCurrentTimeUTC().toString()
      };
      sendMessage(newMessage);
      setMessage("");

      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }

    if (event.key === "Enter" && event.shiftKey) {
      event.preventDefault();
      setMessage((prev) => prev + "\n");
    }
  };

  const showStopButton = lastMessageFromSelectedUser && !isFromHeader;

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "newchatbottom-border-gradient min-h-11 backdrop-blur-[12px] transition-all duration-200 ease-in-out hover:bg-surface-hover-strong flex items-center",
          isMultiLine && "!rounded-3xl"
        )}
      >
        <AnimatePresence initial={false}>
          <motion.div
            key="input"
            className="flex-grow relative flex items-center"
            layoutScroll
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{
              opacity: { duration: 0.05 },
              layout: {
                type: "spring",
                bounce: 0.15
              }
            }}
          >
            <TextareaAutosize
              data-test={`${isFromHeader ? "header-" : ""}chat-textarea`}
              autoComplete="off"
              value={message}
              ref={inputRef}
              onKeyDown={handleKeyPress}
              onChange={handleInputChange}
              onHeightChange={(height) => setIsMultiLine(height > 44)}
              name="message"
              placeholder={t("askAnything")}
              className={cn(
                "content w-full leading-7 placeholder:text-secondary text-primary font-normal resize-none bg-transparent bg-clip-text",
                `w-full px-6 ${isFromHeader ? "text-lg" : "text-base"} my-2`,
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              )}
              disabled={lastMessageFromSelectedUser || shouldDisable}
              minRows={1}
              maxRows={isFromHeader ? 4 : 5}
            />
          </motion.div>
        </AnimatePresence>
        {showStopButton ? (
          <IconButton
            variant="iconNoBg"
            size="2xl"
            data-test="chat-stop-button"
            onClick={onStopResponse}
            disabled={isCancelling}
            tooltipText={t("chat.tooltips.stopResponse")}
            className="z-10 self-end hover:!bg-transparent"
          >
            {isCancelling ? <Loader /> : <StopGenerating />}
          </IconButton>
        ) : (
          <IconButton
            variant="iconNoBg"
            size="2xl"
            data-test="chat-send-button"
            disabled={
              message.trim() === "" ||
              lastMessageFromSelectedUser ||
              shouldDisable
            }
            onClick={handleSend}
            className="z-10 self-end hover:!bg-transparent"
          >
            <SendButton />
          </IconButton>
        )}
      </div>
    </div>
  );
}
